import { IRoutingProvider } from '../routing.interface';
import { Coordinates, RoutingOptions, RouteResult, TransportMode } from '../routing.types';

/**
 * Provider de routing utilisant Mapbox Directions API
 * @see https://docs.mapbox.com/api/navigation/directions/
 */
export class MapboxRoutingProvider implements IRoutingProvider {
  readonly name = 'mapbox';
  private readonly accessToken: string;

  constructor() {
    this.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';
    if (!this.accessToken) {
      console.warn('[MapboxRoutingProvider] No access token configured. Set REACT_APP_MAPBOX_ACCESS_TOKEN.');
    }
  }

  /**
   * Mapping des modes de transport vers les profils Mapbox
   */
  private getProfile(mode?: TransportMode): string {
    switch (mode) {
      case 'driving':
        return 'mapbox/driving';
      case 'cycling':
        return 'mapbox/cycling';
      case 'walking':
      default:
        return 'mapbox/walking';
    }
  }

  async getRoute(
    origin: Coordinates,
    destination: Coordinates,
    options?: RoutingOptions
  ): Promise<RouteResult> {
    if (!this.accessToken) {
      console.warn('[MapboxRoutingProvider] No access token, using fallback');
      return this.createFallbackRoute(origin, destination);
    }

    const profile = this.getProfile(options?.mode);

    // Mapbox utilise le format longitude,latitude (inversé par rapport à notre convention)
    const coordinates = `${origin[1]},${origin[0]};${destination[1]},${destination[0]}`;

    const params = new URLSearchParams({
      access_token: this.accessToken,
      geometries: 'geojson', // Plus simple à parser que polyline
      overview: 'full',
      alternatives: options?.alternatives ? 'true' : 'false',
    });

    const url = `https://api.mapbox.com/directions/v5/${profile}/${coordinates}?${params}`;

    const response = await fetch(url);

    if (!response.ok) {
      console.error('[MapboxRoutingProvider] API error:', response.status);
      return this.createFallbackRoute(origin, destination);
    }

    const data = await response.json();

    if (!data.routes || data.routes.length === 0) {
      console.warn('[MapboxRoutingProvider] No routes found, using fallback');
      return this.createFallbackRoute(origin, destination);
    }

    const route = data.routes[0];

    // GeoJSON coordinates sont [lng, lat], on les convertit en [lat, lng]
    const routeCoordinates: Coordinates[] = route.geometry.coordinates.map(
      (coord: [number, number]) => [coord[1], coord[0]] as Coordinates
    );

    return {
      coordinates: routeCoordinates,
      distance: route.distance, // en mètres
      duration: route.duration, // en secondes
      provider: this.name,
    };
  }

  /**
   * Crée une route fallback (ligne droite) si l'API échoue
   */
  private createFallbackRoute(
    origin: Coordinates,
    destination: Coordinates
  ): RouteResult {
    // Calcul distance à vol d'oiseau (Haversine)
    const R = 6371000;
    const dLat = ((destination[0] - origin[0]) * Math.PI) / 180;
    const dLng = ((destination[1] - origin[1]) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((origin[0] * Math.PI) / 180) *
        Math.cos((destination[0] * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    // Estimation durée (marche ~5km/h)
    const duration = (distance / 1000 / 5) * 3600;

    return {
      coordinates: [origin, destination],
      distance,
      duration,
      provider: `${this.name}-fallback`,
    };
  }
}
